import { inject, Injectable, InjectionToken } from '@angular/core';
import { ErrorReporter } from '@mode/shared/types-common';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { isPresent } from 'ts-is-present';
import { IAllFlagsProvider } from './all-flags-provider.interface';
import { AllFlags, FeatureFlagContext } from './flags.types';
import { getAllFlags, identify, init } from './launch-darkly';

export const IAllFlagsProviderToken = new InjectionToken<IAllFlagsProvider>('AllFlagsProvider', {
  providedIn: 'root',
  factory: () => inject(LaunchDarklyFlagsDataService),
});

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyFlagsDataService implements IAllFlagsProvider {
  // There is no loading observable for allFlags$ provided because LD returns
  // so quickly normally, but if it useful that would be easy to do by adding
  // another notifier and triggering it on the init/update functions
  allFlags$: Observable<AllFlags>;
  private client: LDClient | undefined;
  private allFlagsNotifier: BehaviorSubject<AllFlags | null>;
  private context: FeatureFlagContext | undefined;

  constructor(private bugsnag: BugsnagService, private window: Window, private errorReporter: ErrorReporter) {
    this.allFlagsNotifier = new BehaviorSubject<AllFlags | null>(null);
    this.allFlags$ = this.allFlagsNotifier.pipe(filter(isPresent));
  }

  /** The last context passed into either init or update */
  get currentContext() {
    return this.context;
  }

  /**
   * returns a unique identifier for the user. If anonymous,
   * it will be a UUID, otherwise it should be the username.
   **/
  get userKey(): string | undefined {
    return this.client?.getUser().key;
  }

  /**
   * Initialize the LD client, if not done previously, and retrieve all flags.
   * @param {string} sdkKey the LD client SDK key
   * @param {string} username username of the current user
   * @param {FeatureFlagContext} context
   */
  async init(sdkKey: string, username: string | undefined, context: FeatureFlagContext): Promise<void> {
    if (this.client == null) {
      try {
        const bootstrapText = this.window.document.head.querySelector('#featureFlagState')?.textContent;
        this.client = init(sdkKey, username, context, bootstrapText ? JSON.parse(bootstrapText) : undefined);

        const flags = await getAllFlags(this.client);
        this.context = context;

        // add feature flag data to bugsnag
        this.bugsnag.addMetadata(
          'feature_flags',
          Object.keys(flags).filter((name: string) => flags[name])
        );
        this.allFlagsNotifier.next(flags as AllFlags); // LDFlagSet happens to match up with AllFlags interface
      } catch (err: any) {
        this.errorReporter.notify({ error: err });

        // to prevent blocking the app, make all the flags false
        this.allFlagsNotifier.next({} as AllFlags);
      }
    }
  }

  async update(username: string | undefined, context: FeatureFlagContext) {
    if (this.client != null) {
      this.allFlagsNotifier.next(null); // Blank out the flags so guards and resolvers will wait
      const flags = await identify(this.client, username, context);
      this.context = context;
      this.allFlagsNotifier.next(flags as AllFlags);
    }
  }

  // for use within embed context
  manualInit(flags: AllFlags) {
    this.allFlagsNotifier.next(flags as AllFlags);
  }
}
